import { TIPO_PF, TIPO_PJ } from '@/api/clientes';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { maskCnpj, maskCpf } from '@/lib/formatters';

import { CampoClienteDe } from './CampoClienteDe';
import type { ClienteFormData } from './clienteFormUtils';

interface StepIdentificacaoProps {
  form: ClienteFormData;
  errors: Record<string, string>;
  set: (field: keyof ClienteFormData, value: string) => void;
  isPF: boolean;
}

export function StepIdentificacao({ form, errors, set, isPF }: StepIdentificacaoProps) {
  return (
    <div className="space-y-4">
      {/* Tipo de pessoa */}
      <div className="flex gap-2">
        <Button
          type="button"
          variant={isPF ? 'default' : 'outline'}
          size="sm"
          onClick={() => { set('tipo_pessoa', TIPO_PF); document.getElementById('nome')?.focus(); }}
        >
          Pessoa Física
        </Button>
        <Button
          type="button"
          variant={!isPF ? 'default' : 'outline'}
          size="sm"
          onClick={() => { set('tipo_pessoa', TIPO_PJ); document.getElementById('nome')?.focus(); }}
        >
          Pessoa Jurídica
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="nome">{isPF ? 'Nome Completo' : 'Nome Fantasia'} *</Label>
          <Input
            id="nome"
            autoFocus
            value={form.nome}
            onChange={(e) => set('nome', e.target.value)}
            placeholder={isPF ? 'Nome completo' : 'Nome fantasia'}
            className={errors.nome ? 'border-destructive' : ''}
            aria-invalid={!!errors.nome}
          />
          {errors.nome && <p className="text-xs text-destructive">{errors.nome}</p>}
        </div>

        {isPF ? (
          <>
            <div className="space-y-1.5">
              <Label htmlFor="cpf">CPF *</Label>
              <Input
                id="cpf"
                value={form.cpf}
                onChange={(e) => set('cpf', maskCpf(e.target.value))}
                placeholder="000.000.000-00"
                className={errors.cpf ? 'border-destructive' : ''}
                aria-invalid={!!errors.cpf}
              />
              {errors.cpf && <p className="text-xs text-destructive">{errors.cpf}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rg">RG</Label>
              <Input
                id="rg"
                value={form.rg}
                onChange={(e) => set('rg', e.target.value)}
                placeholder="RG"
              />
            </div>
          </>
        ) : (
          <>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="razao_social">Razão Social</Label>
              <Input
                id="razao_social"
                value={form.razao_social}
                onChange={(e) => set('razao_social', e.target.value)}
                placeholder="Razão social"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cnpj">CNPJ *</Label>
              <Input
                id="cnpj"
                value={form.cnpj}
                onChange={(e) => set('cnpj', maskCnpj(e.target.value))}
                placeholder="00.000.000/0000-00"
                className={errors.cnpj ? 'border-destructive' : ''}
                aria-invalid={!!errors.cnpj}
              />
              {errors.cnpj && <p className="text-xs text-destructive">{errors.cnpj}</p>}
            </div>
          </>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="status">Status</Label>
          <Select value={form.status} onValueChange={(v) => set('status', v)}>
            <SelectTrigger id="status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="PROSPECTO">Prospecto</SelectItem>
              <SelectItem value="ATIVO">Ativo</SelectItem>
              <SelectItem value="INATIVO">Inativo</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <CampoClienteDe form={form} set={set} />
      </div>
    </div>
  );
}
